import React from 'react';
import { useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { TextContentSidebarContext } from '@/components/text-content-lightbox/types/TextContentLightbox';
import { PerplexityContent, PerplexitySource } from '../types/PerplexityContent';
import { PerplexityParametersSection } from './PerplexityParametersSection';
import { PerplexityNavigationButtons } from './PerplexityNavigationButtons';
import { PerplexityActionButtons } from './PerplexityActionButtons';
import StorageButton from '@/components/universal-storage/StorageButton';
import ResearchAddToProjectButton from '@/components/private-research-generator/AddToProjectButton';
import { PublicResearchAddToProjectButton } from '@/components/research-generator';

interface PerplexitySidebarRendererProps {
  content: PerplexityContent;
  context: TextContentSidebarContext<PerplexityContent>;
  onRelatedQuestionClick?: (question: string) => void;
  onDelete?: (content: PerplexityContent) => Promise<void>;
  onDownload?: (content: PerplexityContent) => void;
  checkOwnership?: (content: PerplexityContent) => Promise<boolean>;
  isGeneratingRelatedQuestion?: boolean;
  onGenerationStateChange?: (isGenerating: boolean) => void;
  contentType?: 'research' | 'private_research'; // NEW: Optional prop for content type
  showRelatedQuestions?: boolean; // NEW: Optional prop to hide related questions section
}

// Sidebar Loading Skeleton Component
const SidebarLoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Navigation Buttons Skeleton */}
      <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
        <div className="flex gap-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 flex-1" />
        </div>
      </div>

      {/* Parameters Section Skeleton */}
      <div className="flex-1 space-y-3">
        <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
          <Skeleton className="h-5 w-32 mb-3" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        </div>
        
        <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
          <Skeleton className="h-5 w-40 mb-3" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </div>

      {/* Action Buttons Skeleton */}
      <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
};

export const PerplexitySidebarRenderer: React.FC<PerplexitySidebarRendererProps> = ({
  content,
  context,
  onRelatedQuestionClick,
  onDelete,
  onDownload,
  checkOwnership,
  isGeneratingRelatedQuestion = false,
  onGenerationStateChange,
  contentType = 'research', // Default to 'research' for backward compatibility
  showRelatedQuestions = true, // Default to true for backward compatibility
}) => {
  const location = useLocation();
  const isStoragePage = location.pathname.includes('/storage');
  
  // Show loading skeleton when content is loading
  if (context.isLoading) {
    return <SidebarLoadingSkeleton />;
  }

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Navigation Buttons */}
      <PerplexityNavigationButtons
        hasPrevious={context.hasPrevious}
        hasNext={context.hasNext}
        isLoadingMore={context.isLoadingMore}
        hasNextPage={context.hasNextPage}
        onPrevious={context.onPrevious}
        onNext={context.onNext}
        onLoadMore={context.onLoadMore}
      />

      {/* Storage Button - Only show on storage pages, not on private generator pages */}
      {isStoragePage && (
        <div>
          <StorageButton 
            itemId={content.id}
            contentType={contentType}
            variant="compact"
            gradientFrom="from-green-50/80 to-emerald-50/80 dark:from-green-950/30 dark:to-emerald-950/30"
            gradientTo="hover:from-green-100/80 hover:to-emerald-100/80 dark:hover:from-green-900/40 dark:hover:to-emerald-900/40"
            accentColor="from-green-400 to-emerald-500"
            iconColor="text-green-600 dark:text-green-400"
            borderColor="border-green-200/60 dark:border-green-700/40"
            hoverBorderColor="hover:border-green-300/70 dark:hover:border-green-600/50"
            addText="Save Research"
            manageText="Manage Research"
            addDescription="Add to your storage"
            manageDescription="Research saved"
            dropdownLabel="Save to Storage"
          />
        </div>
      )}

      {/* Add to Project Button - Right below storage */}
      <div>
        {contentType === 'private_research' ? (
          <ResearchAddToProjectButton 
            itemId={content.id}
            variant="compact"
            dropdownLabel="Add to Project"
            gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
            gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
            accentColor="from-purple-400 to-violet-500"
            iconColor="text-purple-600 dark:text-purple-400"
            borderColor="border-purple-200/60 dark:border-purple-700/40"
            hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
            addText="Add to Project"
            manageText="Manage Project"
            addDescription="Assign to a project"
            manageDescription="Project assigned"
          />
        ) : (
          <PublicResearchAddToProjectButton 
            itemId={content.id}
            variant="compact"
            dropdownLabel="Add to Project"
            gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
            gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
            accentColor="from-purple-400 to-violet-500"
            iconColor="text-purple-600 dark:text-purple-400"
            borderColor="border-purple-200/60 dark:border-purple-700/40"
            hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
            addText="Add to Project"
            manageText="Add to Project"
            addDescription="Copy to a project"
            manageDescription="Copy to a project"
          />
        )}
      </div>

      {/* Parameters and Content - NO scroll container */}
      <div className="flex-1 space-y-3">
        <PerplexityParametersSection
          content={content}
          onRelatedQuestionClick={onRelatedQuestionClick}
          isGeneratingRelatedQuestion={isGeneratingRelatedQuestion}
          onGenerationStateChange={onGenerationStateChange}
          contentType={contentType}
          showRelatedQuestions={showRelatedQuestions}
        />
      </div>

      {/* Action Buttons */}
      <div className="rounded-2xl bg-background/80 p-3 border border-accent/20">
        <PerplexityActionButtons
          content={content}
          onDelete={onDelete}
          onDownload={onDownload}
          checkOwnership={checkOwnership}
        />
      </div>
    </div>
  );
}; 